import { useEffect } from 'react';
import { useAlertsStore } from '@/stores/alerts-store';
import { usePatrolsStore } from '@/stores/patrols-store';
import { ALERT_CONFIG, TICKET_STATUS_LABELS, PATROL_STATUS_LABELS } from '@asi/shared';
import { StatusBadge } from '@/components/StatusBadge';
import { Card } from '@/components/ui/card';
import { formatDistanceToNow, isToday } from '@/utils/date';

export function DashboardPage() {
  const { tickets, fetchTickets, subscribeToTickets } = useAlertsStore();
  const { patrols, fetchPatrols, subscribeToPatrolLocations } = usePatrolsStore();

  useEffect(() => {
    fetchTickets();
    fetchPatrols();
    const unsubTickets = subscribeToTickets();
    const unsubPatrols = subscribeToPatrolLocations();
    return () => { unsubTickets(); unsubPatrols(); };
  }, [fetchTickets, fetchPatrols, subscribeToTickets, subscribeToPatrolLocations]);

  const activeTickets = tickets.filter((t) => !['CLOSED', 'CANCELLED'].includes(t.status));
  const availablePatrols = patrols.filter((p) => p.status === 'AVAILABLE' || p.status === 'BACKGROUND');
  const todayTickets = tickets.filter((t) => isToday(t.created_at));

  return (
    <div className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <img
          src="/logo_dark.png"
          alt="Corporación ASI"
          className="h-10 w-auto object-contain dark:block hidden"
        />
        <img
          src="/LOGO ASI.png"
          alt="Corporación ASI"
          className="h-10 w-auto object-contain dark:hidden block"
        />
        <h2 className="text-2xl font-bold text-foreground">Dashboard</h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Alertas Activas" value={activeTickets.length} color="text-red-500 dark:text-red-400" icon="🚨" />
        <StatCard label="Patrulleros Activos" value={patrols.length} color="text-green-600 dark:text-green-400" icon="🛡️" />
        <StatCard label="Disponibles" value={availablePatrols.length} color="text-primary dark:text-blue-400" icon="✅" />
        <StatCard label="Hoy" value={todayTickets.length} color="text-secondary" icon="📊" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent alerts */}
        <Card className="lg:col-span-2 p-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">Alertas Recientes</h3>
          <div className="space-y-2">
            {activeTickets.slice(0, 10).map((ticket) => (
              <div
                key={ticket.id}
                className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: ALERT_CONFIG[ticket.type].color }}
                  />
                  <div>
                    <p className="text-foreground text-sm font-medium">
                      {ticket.ticket_number} — {ALERT_CONFIG[ticket.type].label}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {ticket.address ?? 'Sin dirección'} · {formatDistanceToNow(ticket.created_at)}
                    </p>
                  </div>
                </div>
                <StatusBadge status={ticket.status} />
              </div>
            ))}
            {activeTickets.length === 0 && (
              <p className="text-muted-foreground/60 text-center py-8">No hay alertas activas</p>
            )}
          </div>
        </Card>

        {/* Patrol status */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">Patrulleros</h3>
          <div className="space-y-2">
            {patrols.slice(0, 8).map((patrol) => (
              <div key={patrol.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="text-foreground text-sm font-medium">{patrol.patrol?.full_name ?? 'Patrullero'}</p>
                  <p className="text-muted-foreground text-xs">
                    {patrol.speed != null ? `${Math.round(patrol.speed)} km/h` : ''}
                    {patrol.battery_level != null ? ` · 🔋${patrol.battery_level}%` : ''}
                  </p>
                </div>
                <StatusBadge status={patrol.status} />
              </div>
            ))}
            {patrols.length === 0 && (
              <p className="text-muted-foreground/60 text-center py-8">No hay patrulleros activos</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon }: { label: string; value: number; color: string; icon: string }) {
  return (
    <Card className="p-4 hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-2">
        <p className="text-muted-foreground text-sm">{label}</p>
        <span className="text-xl">{icon}</span>
      </div>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
    </Card>
  );
}
